import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token") || "";
    setToken(t);
  }, [params]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match.");
      return;
    }
    if (!token) {
      setErr("Missing reset token. Please request a new link.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div className="bps-ambient" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-[#9AAEC6] mb-6 hover:text-white transition-colors"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        <div className="mb-8">
          <div className="bps-eyebrow">Reset password</div>
          <h1 className="font-display text-3xl font-light tracking-tighter mt-2 text-white">
            Choose a new password.
          </h1>
        </div>

        {!done ? (
          <form onSubmit={submit} className="space-y-4 bps-card p-6" data-testid="reset-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#7C8FA8]">New password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="reset-password"
                className="w-full bg-white/5 rounded-2xl px-4 py-4 mt-1 outline-none focus:ring-2 focus:ring-[#22D3EE] transition-all"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#7C8FA8]">Confirm</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="reset-confirm"
                className="w-full bg-white/5 rounded-2xl px-4 py-4 mt-1 outline-none focus:ring-2 focus:ring-[#22D3EE] transition-all"
              />
            </div>
            {err && <div className="text-sm text-[#F87171]" data-testid="reset-error">{err}</div>}
            <button
              disabled={loading}
              data-testid="reset-submit"
              className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
            >
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        ) : (
          <div className="bps-card p-6 space-y-3 text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-[#22D3EE]/15 flex items-center justify-center mx-auto">
              <ShieldCheck size={24} className="text-[#22D3EE]" strokeWidth={1.6} />
            </div>
            <div className="font-display text-lg text-white">Password updated</div>
            <div className="text-xs text-[#9AAEC6]">Redirecting to sign in…</div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
